import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { useAuthStore } from "@/stores/auth-store";
import { DashboardLayout } from "@/layouts/dashboard-layout";
import { LoginPage } from "@/features/auth/login-page";

// Code-split every page into its own chunk (replaces Next's per-route splitting).
const OverviewPage = lazy(() => import("@/features/dashboard/overview-page").then((m) => ({ default: m.OverviewPage })));
const p = (name: string) => lazy(() => import("@/features/pages").then((m) => ({ default: (m as any)[name] })));
const MerchantsPage = p("MerchantsPage");
const ProvidersPage = p("ProvidersPage");
const TransactionsPage = p("TransactionsPage");
const SettlementsPage = p("SettlementsPage");
const WebhooksPage = p("WebhooksPage");
const AuditPage = p("AuditPage");
const AccountsPage = p("AccountsPage");
const SystemConfigPage = p("SystemConfigPage");
const WalletsPage = p("WalletsPage");
const SubscribersPage = lazy(() => import("@/features/subscribers").then((m) => ({ default: m.SubscribersPage })));
const PlansPage = lazy(() => import("@/features/plans").then((m) => ({ default: m.PlansPage })));
const InvoicesPage = lazy(() => import("@/features/invoices").then((m) => ({ default: m.InvoicesPage })));
const CommissionPage = lazy(() => import("@/features/commission").then((m) => ({ default: m.CommissionPage })));
const RevenuePage = lazy(() => import("@/features/platform-revenue").then((m) => ({ default: m.RevenuePage })));
const VisitorAnalyticsPage = lazy(() => import("@/features/visitor-analytics").then((m) => ({ default: m.VisitorAnalyticsPage })));
const ProviderBalancesPage = lazy(() => import("@/features/provider-balances").then((m) => ({ default: m.ProviderBalancesPage })));
const DisputesPage = lazy(() => import("@/features/disputes").then((m) => ({ default: m.DisputesPage })));
const SupportPage = lazy(() => import("@/features/support").then((m) => ({ default: m.SupportPage })));
const RoutingCenterPage = lazy(() => import("@/features/routing-center").then((m) => ({ default: m.RoutingCenterPage })));
const SubscriberRoutingCenter = lazy(() => import("@/features/subscriber-routing-center").then((m) => ({ default: m.SubscriberRoutingCenter })));
const MerchantRoutingPage = lazy(() => import("@/features/merchant-routing").then((m) => ({ default: m.MerchantRoutingPage })));
const MerchantDetailPage = lazy(() => import("@/features/merchant-detail").then((m) => ({ default: m.MerchantDetailPage })));
const CountryProfilesPage = lazy(() => import("@/features/country-profiles").then((m) => ({ default: m.CountryProfilesPage })));
const WithdrawalsPage = lazy(() => import("@/features/withdrawals").then((m) => ({ default: m.WithdrawalsPage })));
const CollectorsPage = lazy(() => import("@/features/collectors").then((m) => ({ default: m.CollectorsPage })));
const CollectorWalletsPage = lazy(() => import("@/features/collector-wallets").then((m) => ({ default: m.CollectorWalletsPage })));

function ProtectedRoute() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated ? <Outlet /> : <Navigate to="/login" replace />;
}

/** Landing redirect — provider-admins are scoped to disputes; others to the dashboard. */
function HomeRedirect() {
  const role = useAuthStore((s) => s.admin?.role);
  const home = role === "PROVIDER_ADMIN" ? "/disputes" : "/dashboard";
  return <Navigate to={home} replace />;
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<div className="text-sm text-muted-foreground">Loading…</div>}>{children}</Suspense>;
}

/** /routing is role-split: subscribers get their own tenant-scoped control centre;
 *  the operator's Routing Center controls shared provider infra (owner-only). */
function RoutingHome() {
  const role = useAuthStore((s) => s.admin?.role) ?? "";
  return role.startsWith("SUBSCRIBER_")
    ? <Lazy><SubscriberRoutingCenter /></Lazy>
    : <Lazy><RoutingCenterPage /></Lazy>;
}

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <DashboardLayout />,
        children: [
          { path: "/", element: <HomeRedirect /> },
          { path: "/dashboard", element: <Lazy><OverviewPage /></Lazy> },
          { path: "/subscribers", element: <Lazy><SubscribersPage /></Lazy> },
          { path: "/plans", element: <Lazy><PlansPage /></Lazy> },
          { path: "/invoices", element: <Lazy><InvoicesPage /></Lazy> },
          { path: "/commission", element: <Lazy><CommissionPage /></Lazy> },
          { path: "/revenue", element: <Lazy><RevenuePage /></Lazy> },
          { path: "/merchants", element: <Lazy><MerchantsPage /></Lazy> },
          // Declared after /merchants so the list keeps the bare path.
          { path: "/merchants/:id", element: <Lazy><MerchantDetailPage /></Lazy> },
          { path: "/country-profiles", element: <Lazy><CountryProfilesPage /></Lazy> },
          { path: "/providers", element: <Lazy><ProvidersPage /></Lazy> },
          { path: "/provider-balances", element: <Lazy><ProviderBalancesPage /></Lazy> },
          { path: "/visitor-analytics", element: <Lazy><VisitorAnalyticsPage /></Lazy> },
          { path: "/routing", element: <RoutingHome /> },
          { path: "/routing/merchants", element: <Lazy><MerchantRoutingPage /></Lazy> },
          { path: "/transactions", element: <Lazy><TransactionsPage /></Lazy> },
          { path: "/payouts", element: <Navigate to="/transactions?tab=payout" replace /> },
          { path: "/settlements", element: <Lazy><SettlementsPage /></Lazy> },
          { path: "/withdrawals", element: <Lazy><WithdrawalsPage /></Lazy> },
          { path: "/collector-wallets", element: <Lazy><CollectorWalletsPage /></Lazy> },
          { path: "/collectors", element: <Lazy><CollectorsPage /></Lazy> },
          { path: "/webhooks", element: <Lazy><WebhooksPage /></Lazy> },
          { path: "/disputes", element: <Lazy><DisputesPage /></Lazy> },
          { path: "/support", element: <Lazy><SupportPage /></Lazy> },
          { path: "/wallets", element: <Lazy><WalletsPage /></Lazy> },
          { path: "/connect-accounts", element: <Lazy><AccountsPage /></Lazy> },
          { path: "/system-config", element: <Lazy><SystemConfigPage /></Lazy> },
          { path: "/audit-logs", element: <Lazy><AuditPage /></Lazy> },
        ],
      },
    ],
  },
  { path: "*", element: <HomeRedirect /> },
]);
